/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import DivisionCardV4 from '../../../../../components/cards/DivisionCard/DivisionCardV4';
import { getRegisteredCountDisplay, isGroupPlayEvent, populateDivisionTypesFilterOptions, getDivisionStats, isEventType, EventTypes } from '../../../../../utils/events';
import { eventStateTypeIds, noMaximumLabelValue, sportTypeIds, registrationGroupStatusTypeIds, registrationStatusTypeIds } from '../../../../../utils/eventConstants';
import { setPlayerId } from '../../../../../actions/profile-actions';

import DivisionFilters from './DivisionFilters';
import { filterDivision } from './divisionsTabUtils';
import { resetSelectedDivisionsBasedOnDate, setRegDivisionId, setSelectedDivisionsBasedOnDate } from '../../../../../actions/event-profile-actions';

import ColumnHeader from '../../../../../components/ColumnHeader';

function DivisionsTabContentV4(props) {
    const { clubVerificationFee, isCurrentMemberAdmin, handleRegClick, onDivisionSelect } = props;
    const auth = useSelector(({ auth }) => auth);
    const profile = useSelector(({ profile }) => profile);
    const { event, acceptance } = useSelector(({ eventProfile }) => {
        const { event, acceptance, players } = eventProfile;
        return {
            event,
            acceptance,
            players,
        };
    });
    const dispatch = useDispatch();
    const { selectedDivisionIdsByDate } = useSelector(({ eventProfile }) => eventProfile);
    const { data: organizerProfileData } = profile;

    const isPtsEvent = event.data?.eventTours?.find(tour => tour.name === 'UTR Pro Tennis Tour');
    const sportTypeId = event.data?.sportTypeId;
    const eventStartUtc = event.data?.eventSchedule?.eventStartUtc;
    const eventEndUtc = event.data?.eventSchedule?.eventEndUtc;
    const timeZone = event.data?.eventSchedule?.timeZone;
    const defaultFilterValues = {
        startDate: eventStartUtc ? moment.utc(eventStartUtc).tz(timeZone?.value).startOf('day') : moment(),
        endDate: eventEndUtc ? moment.utc(eventEndUtc).tz(timeZone?.value).endOf('day') : null,
        gender: '',
        sportTypes: {
            tennis: sportTypeId === sportTypeIds.TENNIS,
            pickleball: sportTypeId === sportTypeIds.PICKLEBALL,
        },
        divisionTypes: {},
    };
    const clubId = event.data?.club?.id;

    const [filters, setFilters] = useState(defaultFilterValues);
    const [divisionTypes, setDivisionTypes] = useState([]);
    const [divisionStats, setDivisionStats] = useState({});

    useEffect(() => {
        // get paid hitter profile
        dispatch(setPlayerId(event.data?.eventDirectors?.[0].playerId, false));
    }, []);

    useEffect(() => {
        const divisionTypeOptions = populateDivisionTypesFilterOptions(event.data.eventDivisions);
        const divisionStats = getDivisionStats(event.data.eventDivisions);
        setDivisionTypes(divisionTypeOptions);
        setDivisionStats(divisionStats);
    }, event.data?.eventDivisions);

    const isPaidHitEvent = isEventType(event.data, EventTypes.PAID_HIT);
    const isPaidHitterRegistered = event.data?.registeredMemberIds?.includes(organizerProfileData?.memberId);
    const isRegistrationOpen = event.data?.registrationStateId === eventStateTypeIds.OPEN_REGISTRATION;
    const isPreReg = event.data?.registrationStateId === eventStateTypeIds.PRE_REGISTRATION;

    let showPlayerCount = true;
    if (!isCurrentMemberAdmin && (!event.data?.playerCountVisibility)) {
        showPlayerCount = false;
    }

    const filteredDivisions = (event.data?.eventDivisions || []).filter(division => filterDivision(division, filters, timeZone));

    const isUserRegisteredToDivision = (division) => {
        return !!event.data?.currentMember?.registeredDivisionIds?.includes(division.id);
    };

    const isUserInvitedToDivision = (division) => {
        return event.data.currentMember?.registeredDivisions?.find(div => div.id === division.id &&
             div.registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
             (div.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING || div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER));
    };

    const doesUserHavePreRegInvite = (division) => {
        return event?.data?.currentMember?.memberPreRegistrationInvites?.some(invite => invite.availableDivisionIds.includes(division.id));
    };

    const getRegisteredPlayersText = (division) => {
        const { maxPlayers, registeredCount, divisionFormatTypeId } = division;
        const isAcceptanceList = acceptance?.[division.id] && !acceptance?.[division.id].error;
        const registeredCountDisplay = getRegisteredCountDisplay(registeredCount, divisionFormatTypeId, isPaidHitEvent, isPaidHitterRegistered);

        const showMaxPlayersCount = maxPlayers && maxPlayers !== noMaximumLabelValue;

        if (registeredCountDisplay && registeredCountDisplay > 0) {
            const playersCount = showMaxPlayersCount ? `${registeredCountDisplay}/${maxPlayers}` : registeredCountDisplay;
            const status = isAcceptanceList ? 'registered' : 'playing';

            if (isUserRegisteredToDivision(division)) {
                return `${playersCount} ${status}`;
            }
            return `${playersCount} registered`;
        }
        return ``;
    };

    const showRegisteredText = !(isCurrentMemberAdmin && isPaidHitEvent && isPaidHitterRegistered);
    const noDateAssigned = 'NO DATE ASSIGNED';
    const dateBasedDivisions = {};

    filteredDivisions.forEach((division) => {
        const date = division.specificDivisionDateUtc ? new Date(division.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
        dateBasedDivisions[date] = (dateBasedDivisions[date] || 0) + 1;
    });
    const onRegClick = (e, divisionId, date, divisionsLength) => {
        dispatch(setRegDivisionId(divisionId));

        dispatch(resetSelectedDivisionsBasedOnDate());

        if (divisionsLength && divisionsLength === 1) {
            dispatch(setSelectedDivisionsBasedOnDate({
                [date]: null,
                selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
            }));
            handleRegClick(e);
        } else if (date !== noDateAssigned) {
            dispatch(setSelectedDivisionsBasedOnDate({
                [date]: divisionId,
                selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
            }));
            handleRegClick(e);
        } else {
            handleRegClick(e);
        }
    };
    const registeredDivsWithSpecificDateEnabled = { ids: [], dates: [] };
    filteredDivisions.forEach(division => {
        if (isUserRegisteredToDivision(division) && !!division?.specificDivisionDateEnabled) {
            registeredDivsWithSpecificDateEnabled.ids.push(division?.id);
            registeredDivsWithSpecificDateEnabled.dates.push(division?.specificDivisionDateUtc);
        }
    });
    const registeredDivs = [];
    filteredDivisions.forEach(division => {
        if (isUserRegisteredToDivision(division)) {
            registeredDivs.push(division);
        }
    });

    const hasDivisionWithSpecificDate = filteredDivisions.some((div) => div.specificDivisionDateUtc !== null);
    const gridLayout = hasDivisionWithSpecificDate ? [
        '4fr',
        '2fr',
        '1.5fr',
        '1.5fr',
        '1.5fr',
        '1.5fr',
        '2fr',
        '2fr',
    ]
    : [
        '4fr',
        '1.5fr',
        '1.5fr',
        '1.5fr',
        '1.5fr',
        '2fr',
        '2fr',
    ];

    let registeredDivisionCategory = 0;
    let recentNonNullCategory = null;
    filteredDivisions.forEach((division, index) => {
        if (isUserRegisteredToDivision(division) && !!event?.data?.preventProAmDivisionRegistrationConflicts) {
            if (division?.divisionCategory !== null) {
                recentNonNullCategory = division.divisionCategory;
            }
            registeredDivisionCategory = division?.divisionCategory;
        }
        if (index === filteredDivisions?.length - 1 && registeredDivisionCategory === null) {
            registeredDivisionCategory = recentNonNullCategory;
        }
    });

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <DivisionFilters
                        filters={filters}
                        setFilters={setFilters}
                        defaultFilterValues={defaultFilterValues}
                        searchTypes={{ divisionTypes }}
                        divisionStats={divisionStats}
                        sportTypeId={sportTypeId}
                    />
                </div>
            </div>

            <div className="d-flex text-small fw600 division-tab-header-v4">
                <div className="flex-grow">
                    <div className="row">
                        <div className={`col-12`}>
                            <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.map(fr => `minmax(0, ${fr})`).join(' ') }}>
                                <ColumnHeader
                                    columnName="Division"
                                    data-testid="event-profile.divisions.column-header.division"
                                />
                                {hasDivisionWithSpecificDate && (
                                    <ColumnHeader
                                        columnName={`Date`}
                                        data-testid="event-profile.divisions.column-header.date"
                                    />
                                )}
                                <ColumnHeader
                                    columnName={`Type`}
                                />
                                <ColumnHeader
                                    columnName={sportTypeId === sportTypeIds.PICKLEBALL ? 'UTR-P' : 'UTR'}
                                />
                                <ColumnHeader
                                    columnName={`Age`}
                                />
                                <ColumnHeader
                                    columnName={`Gender`}
                                />
                                <ColumnHeader
                                    columnName={`Price`}
                                />
                                <ColumnHeader
                                    columnName={``}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!isGroupPlayEvent(event.data) && (
                <>
                    <div className="">
                        {filteredDivisions.map((division) => {
                            const date = division?.specificDivisionDateUtc ? new Date(division.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;

                            const isDivisionNotRegistered = !registeredDivsWithSpecificDateEnabled?.ids.includes(division?.id);
                            const isDivisionDateRegistered = registeredDivsWithSpecificDateEnabled?.dates.includes(division?.specificDivisionDateUtc);
                            const preventDateConflicts = !!event?.data?.preventDivisionDateRegistrationConflicts;
                            const preventAmProConflicts = !!event?.data?.preventProAmDivisionRegistrationConflicts;
                            const isCategoryMismatch = preventAmProConflicts && (division?.divisionCategory && registeredDivs.some((div) => ((div.divisionFormatTypeId === division.divisionFormatTypeId) && !!div.divisionCategory && div.divisionCategory !== division.divisionCategory)));
                            const disableRegisterBtn = ((isDivisionNotRegistered && isDivisionDateRegistered) && preventDateConflicts) || isCategoryMismatch;

                            return (
                                <div key={`division-${division.id}`}>
                                    <DivisionCardV4
                                        user={auth.user}
                                        utrTypeId={event.data.utrTypeId}
                                        clubId={clubId}
                                        division={division}
                                        className="cursor-unset"
                                        showFooter={true}
                                        workflowVersion={event.data.workflowVersion}
                                        showDetails={true}
                                        clubVerificationFee={clubVerificationFee}
                                        allowOffPlatformPayments={event.data.allowOffPlatformPayments}
                                        isPaidHitEvent={isPaidHitEvent}
                                        isPaidHitterRegistered={isPaidHitterRegistered}
                                        sportTypeId={sportTypeId}
                                        registeredPlayersText={getRegisteredPlayersText(division)}
                                        isUserRegistered={isUserRegisteredToDivision(division)}
                                        showRegisteredText={showRegisteredText}
                                        handleRegClick={onRegClick}
                                        eventIsPublished={event.data.published}
                                        isRegistrationOpen={isRegistrationOpen}
                                        eventStartUtc={eventStartUtc}
                                        disableRegisterBtn={disableRegisterBtn}
                                        divisionsLength={dateBasedDivisions?.[date]}
                                        gridLayout={gridLayout}
                                        isPtsEvent={isPtsEvent}
                                        showPlayersCount={showPlayerCount}
                                        hasDivisionWithSpecificDate={hasDivisionWithSpecificDate}
                                        onDivisionSelect={onDivisionSelect}
                                        timeZone={timeZone}
                                        event={event?.data}
                                        isUserInvited={isUserInvitedToDivision(division)}
                                        eventSchedule={event?.data?.eventSchedule}
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        isPreReg={isPreReg}
                                        doesUserHavePreRegInvite={doesUserHavePreRegInvite(division)}
                                        data-testid={`event-profile.division-card-${division.id}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {!filteredDivisions.length && (
                        <div className="row divisions-big">
                            <div className="col">
                                No divisions are matching your filter criteria.
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

DivisionsTabContentV4.propTypes = {
    clubVerificationFee: PropTypes.number,
    isCurrentMemberAdmin: PropTypes.bool,
    handleRegClick: PropTypes.func,
    onDivisionSelect: PropTypes.func,
};
DivisionsTabContentV4.defaultProps = {
    clubVerificationFee: 0,
    isCurrentMemberAdmin: false,
    handleRegClick: () => { },
    onDivisionSelect: () => {},
};

export default withRouter(DivisionsTabContentV4);
